"use client";

/* eslint-disable @next/next/no-img-element -- Contextual support previews use authenticated private-file URLs and marketplace-hosted images. */

import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, CircleHelp, Clock3, Command, ImagePlus, LogOut, Menu, MessageSquare, Send, Settings, UserRound, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { isPortalNavItemActive, portalNav } from "@/components/prv/data";
import navStyles from "@/components/prv/account-navigation.module.css";
import { HomeHeader } from "@/components/prv/home-header";
import { MobileAccountDrawer } from "@/components/prv/mobile-account-drawer";
import { MobileProductSearch } from "@/components/prv/mobile-product-search";
import { SiteFooter } from "@/components/prv/site-footer";
import { AuthGuard } from "@/components/prv/auth-guard";
import { usePortalServerAuth } from "@/components/prv/portal-auth-context";
import {
  AUTH_CHANGE_EVENT,
  readAuthSession,
  type AuthSession,
} from "@/lib/auth-session";
import { useCustomerLogout } from "@/lib/use-customer-logout";
import { getUserCenterPageMeta, userCenterNavLabel } from "@/lib/user-center-i18n";
import { useUserCenterLocale } from "@/lib/use-user-center-locale";
import { PORTAL_SUMMARY_EVENT, PORTAL_SUMMARY_STORAGE_KEY } from "@/lib/portal-summary-sync";
import { apiErrorMessage } from "@/lib/api-error";
import {
  completePayloadIdempotencyAttempt,
  getPayloadIdempotencyAttempt,
  type PayloadIdempotencyAttempt,
} from "@/lib/client-idempotency-attempt";
import {
  buildTicketRequest,
  parseSupportContext,
  SUPPORT_STATUS_LABELS,
  supportErrorMessage,
  ticketMatchesContext,
  type SupportContext,
  type SupportTicket,
} from "@/app/user-center/support/support-model";
import { formatRefundMoney, getRefundPresentation, type RefundCase } from "@/app/user-center/after-sales/after-sales-model";
import drawerStyles from "./contextual-support-drawer.module.css";

const portalScrollPositions = new Map<string, number>();
let restorePortalScrollOnNextNavigation = false;

function accountInitials(name: string | undefined) {
  const words = (name || "PRVBUY Member").trim().split(/\s+/).filter(Boolean);
  return words.slice(0, 2).map((word) => word[0]?.toUpperCase()).join("") || "PM";
}

export function BrandMark() {
  return (
    <Link href="/" className="flex items-center gap-3" aria-label="PRVBUY home">
      <span className="flex h-10 w-10 items-center justify-center rounded-[15px] border border-prv-ink/10 bg-prv-accent text-sm font-black text-prv-ink shadow-[0_12px_24px_rgba(8,46,38,0.14)]">
        <Image
          src="/images/prvbuy-logo-p-flat.png"
          alt=""
          width={32}
          height={32}
          className="h-7 w-7 object-contain"
        />
      </span>
      <span>
        <span className="block text-base font-black leading-none tracking-[-0.02em] text-prv-ink">
          PRVBUY
        </span>
        <span className="mt-1 block text-[10px] font-bold leading-none tracking-[-0.005em] text-prv-muted">
          Private Buying Agent
        </span>
      </span>
    </Link>
  );
}

export function SiteHeader() {
  return <HomeHeader />;
}

export function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <>
      <SiteHeader />
      <main className="pt-20">{children}</main>
    </>
  );
}

type SupportDrawerContext = SupportContext;
type SupportDrawerTicket = SupportTicket;

type SupportDrawerOrderPreview = {
  businessLabel?: string;
  orderNo: string;
  productNo?: string;
  title: string;
  sku?: string;
  image?: string;
  unitPrice?: string;
  quantity?: string;
  total?: string;
  status?: string;
};

type UploadedDrawerImage = {
  file: File;
  fileName: string;
  fileUrl: string;
};

const DRAWER_IMAGE_MARKER = /\[\[prvbuy-image:([^|\]]+)\|([^\]]+)\]\]/g;

function readDrawerMessage(body: string) {
  const images: Array<{ fileUrl: string; fileName: string }> = [];
  const text = body.replace(DRAWER_IMAGE_MARKER, (_match, encodedUrl: string, encodedName: string) => {
    try {
      images.push({ fileUrl: decodeURIComponent(encodedUrl), fileName: decodeURIComponent(encodedName) });
    } catch {
      // Keep malformed legacy content visible instead of treating it as an image attachment.
      return _match;
    }
    return "";
  }).trim();
  return { text, images };
}

function drawerMoney(value: unknown) {
  if (!value || typeof value !== "object") return "";
  const money = value as { currency?: unknown; amount?: unknown };
  if (typeof money.currency !== "string" || typeof money.amount !== "number") return "";
  return `${money.currency} ${money.amount.toFixed(2)}`;
}

function drawerStatusLabel(status: string | undefined) {
  return status ? status.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase()) : "";
}

function supportContextFromSearch(search: string): SupportDrawerContext | null {
  return parseSupportContext(search);
}

function contextSubject(context: SupportDrawerContext) {
  return context.subject;
}

function supportContextFromHref(href: string) {
  let url: URL;
  try {
    url = new URL(href, window.location.origin);
  } catch {
    return null;
  }

  if (url.origin !== window.location.origin || url.pathname !== "/user-center/support") return null;
  return supportContextFromSearch(url.search);
}

function ContextualSupportDrawer({
  context,
  initialPreview,
  onClose,
}: {
  context: SupportDrawerContext | null;
  initialPreview: SupportDrawerOrderPreview | null;
  onClose: () => void;
}) {
  const [tickets, setTickets] = useState<SupportDrawerTicket[]>([]);
  const [activeTicketId, setActiveTicketId] = useState("");
  const [orderPreview, setOrderPreview] = useState<SupportDrawerOrderPreview | null>(null);
  const [message, setMessage] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [uploadedImage, setUploadedImage] = useState<UploadedDrawerImage | null>(null);
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingReadback, setPendingReadback] = useState<{ ticketId: string; messageId?: string } | null>(null);
  const writeLockRef = useRef(false);
  const createAttemptRef = useRef<PayloadIdempotencyAttempt | null>(null);
  const messageRef = useRef<HTMLTextAreaElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const activeTicket = activeTicketId ? tickets.find((ticket) => ticket.id === activeTicketId) : undefined;
  const imagePreviewUrl = useMemo(() => imageFile ? URL.createObjectURL(imageFile) : "", [imageFile]);

  const loadDrawerTickets = useCallback(async () => {
    if (!context) return [] as SupportDrawerTicket[];
    setLoading(true);
    setNotice("");
    try {
      const response = await fetch("/api/support/tickets", { cache: "no-store" });
      const payload = await response.json().catch(() => null);
      if (!response.ok || !payload?.ok || !Array.isArray(payload.tickets)) throw new Error(apiErrorMessage(payload, response.status, "Customer Support conversations could not be loaded."));
      const nextTickets = payload.tickets as SupportDrawerTicket[];
      setTickets(nextTickets);
      const contextualTicket = nextTickets.find((ticket) => ticket.status !== "resolved" && ticketMatchesContext(ticket, context));
      setActiveTicketId(contextualTicket?.id ?? "");
      return nextTickets;
    } catch (error) {
      setNotice(supportErrorMessage(error, "Support conversation could not be loaded."));
      throw error;
    } finally {
      setLoading(false);
    }
  }, [context]);

  useEffect(() => {
    if (!context) return;
    const initialize = window.setTimeout(() => {
      setTickets([]);
      setActiveTicketId("");
      setMessage("");
      setImageFile(null);
      setUploadedImage(null);
      setOrderPreview(initialPreview);
      setPendingReadback(null);
      void loadDrawerTickets().catch(() => undefined);
      messageRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(initialize);
  }, [context, initialPreview, loadDrawerTickets]);

  useEffect(() => () => {
    if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
  }, [imagePreviewUrl]);

  useEffect(() => {
    const relatedOrderId = context?.items.find((item) => item.key === "orderId")?.value;
    const relatedRefundId = context?.items.find((item) => item.key === "refundId")?.value;
    if (!relatedOrderId || relatedRefundId) return;

    let active = true;

    fetch(`/api/orders/${encodeURIComponent(relatedOrderId)}`, { cache: "no-store" })
      .then((response) => response.json().then((payload) => ({ ok: response.ok, payload })))
      .then(({ ok, payload }) => {
        if (!active || !ok || !payload?.ok || !payload.detail) return;
        const detail = payload.detail as {
          order?: {
            orderNo?: string;
            quantity?: number;
            status?: string;
            itemCost?: unknown;
            domesticShippingCost?: unknown;
            serviceFee?: unknown;
            totalPaid?: unknown;
          };
          product?: { title?: string; imageUrl?: string; platformProductId?: string };
          sku?: { label?: string; color?: string; size?: string; imageUrl?: string };
        };
        const order = detail.order ?? {};
        const itemCost = drawerMoney(order.itemCost);
        const shippingCost = drawerMoney(order.domesticShippingCost);
        const serviceFee = drawerMoney(order.serviceFee);
        const paidTotal = drawerMoney(order.totalPaid);
        const computedTotal = (() => {
          const values = [order.itemCost, order.domesticShippingCost, order.serviceFee]
            .filter((value): value is { currency?: unknown; amount?: unknown } => Boolean(value && typeof value === "object"));
          const currency = values.find((value) => typeof value.currency === "string")?.currency;
          const amount = values.reduce((sum, value) => sum + (typeof value.amount === "number" ? value.amount : 0), 0);
          return typeof currency === "string" && amount > 0 ? `${currency} ${amount.toFixed(2)}` : "";
        })();

        setOrderPreview({
          businessLabel: "Order No:",
          orderNo: order.orderNo ?? relatedOrderId,
          productNo: detail.product?.platformProductId ?? context.items.find((item) => item.key === "productId")?.value,
          title: detail.product?.title ?? "Order item",
          sku: detail.sku?.label ?? [detail.sku?.color, detail.sku?.size].filter(Boolean).join(" / "),
          image: detail.sku?.imageUrl || detail.product?.imageUrl,
          unitPrice: itemCost,
          quantity: typeof order.quantity === "number" ? `x${order.quantity}` : "",
          total: paidTotal && !paidTotal.endsWith("0.00") ? paidTotal : computedTotal || itemCost || shippingCost || serviceFee,
          status: drawerStatusLabel(order.status),
        });
      })
      .catch(() => {
        if (active) setOrderPreview(null);
      });

    return () => {
      active = false;
    };
  }, [context]);

  useEffect(() => {
    const relatedRefundId = context?.items.find((item) => item.key === "refundId")?.value;
    if (!relatedRefundId) return;

    let active = true;
    fetch(`/api/refunds/${encodeURIComponent(relatedRefundId)}`, { cache: "no-store" })
      .then((response) => response.json().then((payload) => ({ ok: response.ok, payload })))
      .then(({ ok, payload }) => {
        if (!active || !ok || !payload?.ok || !payload.case) return;
        const refund = payload.case as RefundCase;
        const presentation = getRefundPresentation(refund);
        const unitPrice = refund.orderSummary?.unitPrice;
        setOrderPreview({
          businessLabel: "Refund No:",
          orderNo: refund.caseNo ?? refund.id,
          productNo: refund.refundItemId ?? refund.product?.productNo,
          title: refund.product?.name ?? "Refund item",
          sku: refund.product?.sku,
          image: refund.product?.image,
          unitPrice: unitPrice ? formatRefundMoney(unitPrice) : refund.product?.total,
          quantity: `x${refund.quantity ?? refund.orderSummary?.quantity ?? 1}`,
          total: formatRefundMoney(refund.amount),
          status: presentation.statusLabel,
        });
      })
      .catch(() => {
        if (active) setOrderPreview(null);
      });

    return () => {
      active = false;
    };
  }, [context]);

  useEffect(() => {
    if (!context) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [context, onClose]);

  if (!context) return null;

  const confirmDrawerReadback = async (pending: { ticketId: string; messageId?: string }) => {
    setLoading(true);
    setNotice("Confirming the saved message…");
    try {
      const nextTickets = await loadDrawerTickets();
      const ticket = nextTickets.find((item) => item.id === pending.ticketId);
      const confirmed = ticket && (!pending.messageId || ticket.messages.some((item) => item.id === pending.messageId));
      if (!confirmed) throw new Error("SUPPORT_READBACK_PENDING");
      setPendingReadback(null);
      setActiveTicketId(pending.ticketId);
      setMessage("");
      setImageFile(null);
      setUploadedImage(null);
      setNotice("Saved and confirmed from Support.");
    } catch {
      setPendingReadback(pending);
      setNotice("The message was accepted, but the latest server copy could not be confirmed. Retry confirmation; your draft is preserved.");
    } finally {
      writeLockRef.current = false;
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (writeLockRef.current || pendingReadback) return;
    if (!message.trim() && !imageFile) {
      setNotice("Write a message or choose an image before sending.");
      messageRef.current?.focus();
      return;
    }

    writeLockRef.current = true;
    setNotice("");
    setLoading(true);
    try {
      const relatedRefundId = context.items.find((item) => item.key === "refundId")?.value;
      let nextUploadedImage = uploadedImage?.file === imageFile ? uploadedImage : null;
      if (imageFile && !nextUploadedImage) {
        if (!relatedRefundId) throw new Error("SUPPORT_IMAGE_REQUIRES_AFTER_SALES_CONTEXT");
        const formData = new FormData();
        formData.append("file", imageFile);
        formData.append("note", "Image sent in the linked Support conversation.");
        const uploadResponse = await fetch(`/api/refunds/${encodeURIComponent(relatedRefundId)}/attachments`, {
          method: "POST",
          body: formData,
        });
        const uploadPayload = await uploadResponse.json().catch(() => null);
        if (!uploadResponse.ok || !uploadPayload?.ok || !uploadPayload.attachment?.fileUrl) {
          throw new Error(uploadPayload?.error ?? "SUPPORT_IMAGE_UPLOAD_FAILED");
        }
        nextUploadedImage = {
          file: imageFile,
          fileName: String(uploadPayload.attachment.fileName ?? imageFile.name),
          fileUrl: String(uploadPayload.attachment.fileUrl),
        };
        setUploadedImage(nextUploadedImage);
      }
      const imageMarker = nextUploadedImage
        ? `[[prvbuy-image:${encodeURIComponent(nextUploadedImage.fileUrl)}|${encodeURIComponent(nextUploadedImage.fileName)}]]`
        : "";
      const outgoingMessage = [message.trim(), imageMarker].filter(Boolean).join("\n");
      let pending: { ticketId: string; messageId?: string };
      if (activeTicket) {
        const response = await fetch(`/api/support/tickets/${encodeURIComponent(activeTicket.id)}/messages`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ message: outgoingMessage }),
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok || !payload?.ok || !payload.message?.id) throw new Error(apiErrorMessage(payload, response.status, "Your Support message could not be sent."));
        pending = { ticketId: activeTicket.id, messageId: String(payload.message.id) };
      } else {
        const requestPayload = buildTicketRequest({
          subject: contextSubject(context),
          category: context.category,
          message: outgoingMessage,
          context,
        });
        const attempt = getPayloadIdempotencyAttempt("support-drawer-create", requestPayload, createAttemptRef.current);
        createAttemptRef.current = attempt;
        const response = await fetch("/api/support/tickets", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Idempotency-Key": attempt.key },
          body: JSON.stringify(requestPayload),
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok || !payload?.ok || !payload.ticket?.id) throw new Error(apiErrorMessage(payload, response.status, "Your Support request could not be created."));
        completePayloadIdempotencyAttempt("support-drawer-create", attempt);
        createAttemptRef.current = null;
        pending = { ticketId: String(payload.ticket.id) };
      }
      setPendingReadback(pending);
      await confirmDrawerReadback(pending);
    } catch (error) {
      writeLockRef.current = false;
      setLoading(false);
      setNotice(supportErrorMessage(error, "Message could not be sent. Your draft is still here."));
    }
  };

  return (
    <div className="portal-help-drawer" role="dialog" aria-modal="true" aria-label="Get Help">
      <button type="button" className="portal-help-drawer-backdrop" aria-label="Close help" onClick={onClose} />
      <aside className={`portal-help-drawer-panel ${drawerStyles.panel}`}>
        <header>
          <div>
            <span>Get Help</span>
            <h2>Get Help</h2>
          </div>
          <button type="button" aria-label="Close help" onClick={onClose}>
            <X aria-hidden="true" className={drawerStyles.desktopCloseIcon} />
            <ArrowLeft aria-hidden="true" className={drawerStyles.mobileBackIcon} />
          </button>
        </header>

        <div className={`portal-help-hours ${drawerStyles.hours}`}>
          <Clock3 aria-hidden="true" />
          Messages are stored in your private Support ticket.
        </div>

        <section className={`portal-help-context-card ${drawerStyles.contextCard}`} aria-label="Request context">
          {orderPreview ? (
            <div className="portal-help-order-card">
              {orderPreview.image ? <img src={orderPreview.image} alt="" /> : null}
              <div>
                <span>{orderPreview.businessLabel ?? "Order No:"} {orderPreview.orderNo}</span>
                <strong>{orderPreview.title}</strong>
                {orderPreview.sku ? <p>{orderPreview.sku}</p> : null}
                <dl>
                  {orderPreview.unitPrice ? <div><dt>Unit Price</dt><dd>{orderPreview.unitPrice}</dd></div> : null}
                  {orderPreview.quantity ? <div><dt>Quantity</dt><dd>{orderPreview.quantity}</dd></div> : null}
                  {orderPreview.total ? <div><dt>Total</dt><dd>{orderPreview.total}</dd></div> : null}
                </dl>
              </div>
              {orderPreview.status ? <em>{orderPreview.status}</em> : null}
            </div>
          ) : (
            <>
              <div>
                <CircleHelp aria-hidden="true" />
                <strong>{contextSubject(context)}</strong>
              </div>
              <dl>
                {context.items.map((item) => (
                  <div key={`${item.label}-${item.value}`}>
                    <dt>{item.label}</dt>
                    <dd>{item.value}</dd>
                  </div>
                ))}
              </dl>
            </>
          )}
          {context.items.some((item) => item.persistence === "linked") ? (
            <p><strong>Linked to this ticket:</strong> {context.items.filter((item) => item.persistence === "linked").map((item) => item.label).join(", ")}.</p>
          ) : null}
          {context.items.some((item) => item.persistence === "message") ? (
            <p><strong>Included in your message:</strong> {context.items.filter((item) => item.persistence === "message").map((item) => item.label).join(", ")}.</p>
          ) : null}
        </section>

        <section className={`portal-help-chat ${drawerStyles.chat}`}>
          {activeTicket ? <span className="portal-help-ticket-status">{SUPPORT_STATUS_LABELS[activeTicket.status]}</span> : null}
          {activeTicket?.messages.length ? (
            activeTicket.messages.map((item) => {
              const content = readDrawerMessage(item.body);
              return (
                <article key={item.id} data-sender={item.senderType}>
                  <b>{item.senderType === "customer" ? "You" : "PRVBUY Support"}</b>
                  {content.text ? <p>{content.text}</p> : null}
                  {content.images.map((image) => (
                    <a key={`${item.id}-${image.fileUrl}`} className={drawerStyles.messageImage} href={image.fileUrl} target="_blank" rel="noreferrer">
                      <img src={image.fileUrl} alt={image.fileName} />
                    </a>
                  ))}
                  <time>{item.createdAt.replace("T", " ").slice(0, 19)}</time>
                </article>
              );
            })
          ) : (
            <div className="portal-help-empty">
              <MessageSquare aria-hidden="true" />
              <strong>Tell us what you need help with.</strong>
              <p>{context.items.some((item) => item.persistence === "linked") ? "Supported business details will be linked to the ticket." : "The displayed context will be included in your first message."}</p>
            </div>
          )}
        </section>

        <footer className={drawerStyles.composer}>
          {notice ? <p>{notice}{pendingReadback ? <button type="button" onClick={() => { if (writeLockRef.current) return; writeLockRef.current = true; void confirmDrawerReadback(pendingReadback); }}>Retry confirmation</button> : !tickets.length && !loading ? <button type="button" onClick={() => void loadDrawerTickets().catch(() => undefined)}>Retry</button> : null}</p> : null}
          {imageFile && imagePreviewUrl ? (
            <div className={drawerStyles.imageDraft}>
              <img src={imagePreviewUrl} alt="Selected attachment preview" />
              <span>{imageFile.name}</span>
              <button type="button" aria-label="Remove selected image" onClick={() => { setImageFile(null); setUploadedImage(null); }}><X aria-hidden="true" /></button>
            </div>
          ) : null}
          <div className={drawerStyles.composerRow}>
            <textarea
              ref={messageRef}
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              placeholder="Please enter"
              onKeyDown={(event) => {
                if ((event.metaKey || event.ctrlKey) && event.key === "Enter") void sendMessage();
              }}
            />
            <input
              ref={imageInputRef}
              className={drawerStyles.fileInput}
              type="file"
              tabIndex={-1}
              aria-hidden="true"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={(event) => {
                const file = event.target.files?.[0] ?? null;
                event.target.value = "";
                if (!file) return;
                if (!file.type.startsWith("image/") || file.size > 8 * 1024 * 1024) {
                  setNotice("Choose a JPG, PNG, WEBP or GIF image up to 8 MB.");
                  return;
                }
                setNotice("");
                setUploadedImage(null);
                setImageFile(file);
              }}
            />
            <button type="button" className={drawerStyles.imageButton} aria-label="Choose an image" onClick={() => imageInputRef.current?.click()} disabled={loading || Boolean(pendingReadback)}>
              <ImagePlus aria-hidden="true" />
            </button>
            <button type="button" className={drawerStyles.sendIconButton} aria-label={loading ? "Sending message" : "Send message"} onClick={sendMessage} disabled={loading || Boolean(pendingReadback) || (!message.trim() && !imageFile)}>
              <Send aria-hidden="true" />
            </button>
          </div>
          <div className={drawerStyles.desktopActions}>
            <button type="button" onClick={onClose}>Cancel</button>
            <button type="button" onClick={sendMessage} disabled={loading || Boolean(pendingReadback) || (!message.trim() && !imageFile)}>
              <Send aria-hidden="true" />
              {loading ? "Sending…" : "Send"}
            </button>
          </div>
        </footer>
      </aside>
    </div>
  );
}

export function PortalShell({
  children,
  title,
  eyebrow,
  requireAuth = true,
}: {
  children: React.ReactNode;
  title: string;
  eyebrow: string;
  requireAuth?: boolean;
}) {
  const pathname = usePathname();
  const serverAuthVerified = usePortalServerAuth();
  const [session, setSession] = useState<AuthSession | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [mobileSearchMode, setMobileSearchMode] = useState<"product" | "image">("product");
  const [supportDrawerContext, setSupportDrawerContext] = useState<SupportDrawerContext | null>(null);
  const [supportDrawerPreview, setSupportDrawerPreview] = useState<SupportDrawerOrderPreview | null>(null);
  const [navCounts, setNavCounts] = useState<{ refundsPending: number; warehousePackable: number } | null>(null);
  const portalMainRef = useRef<HTMLElement | null>(null);
  const contentPaneRef = useRef<HTMLDivElement | null>(null);
  const { isSigningOut, signOut } = useCustomerLogout();
  const { locale, copy: userCenterCopy } = useUserCenterLocale();
  const showsMobileBack = pathname !== "/user-center" && pathname !== "/dashboard";
  const localizedMeta = getUserCenterPageMeta(locale, pathname === "/dashboard" ? "/user-center" : pathname);
  const localizedEyebrow = localizedMeta?.eyebrow ?? eyebrow;
  const localizedTitle = localizedMeta?.title ?? title;
  useEffect(() => {
    const syncSession = () => setSession(readAuthSession());
    syncSession();
    window.addEventListener(AUTH_CHANGE_EVENT, syncSession);
    window.addEventListener("storage", syncSession);
    return () => {
      window.removeEventListener(AUTH_CHANGE_EVENT, syncSession);
      window.removeEventListener("storage", syncSession);
    };
  }, []);

  const loadNavCounts = useCallback(async () => {
    if (!readAuthSession()) { setNavCounts(null); return; }
    try {
      const response = await fetch("/api/portal/summary", { cache: "no-store" });
      const payload = await response.json().catch(() => null);
      if (!response.ok || !payload?.ok || !Number.isSafeInteger(payload.refundsPending) || !Number.isSafeInteger(payload.warehousePackable)) throw new Error("PORTAL_SUMMARY_INVALID");
      setNavCounts({ refundsPending: payload.refundsPending, warehousePackable: payload.warehousePackable });
    } catch {
      setNavCounts(null);
    }
  }, []);

  useEffect(() => {
    const initial = window.setTimeout(() => { void loadNavCounts(); }, 0);
    const refresh = () => { void loadNavCounts(); };
    const onStorage = (event: StorageEvent) => { if (event.key === PORTAL_SUMMARY_STORAGE_KEY) refresh(); };
    const onVisibility = () => { if (document.visibilityState === "visible") refresh(); };
    window.addEventListener(PORTAL_SUMMARY_EVENT, refresh);
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearTimeout(initial);
      window.removeEventListener(PORTAL_SUMMARY_EVENT, refresh);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [loadNavCounts, pathname]);

  useEffect(() => {
    const markHistoryNavigation = () => {
      restorePortalScrollOnNextNavigation = true;
    };

    window.addEventListener("popstate", markHistoryNavigation);
    return () => window.removeEventListener("popstate", markHistoryNavigation);
  }, []);

  useEffect(() => {
    const pane = contentPaneRef.current;
    if (!pane) return;

    const frame = window.requestAnimationFrame(() => {
      pane.scrollTop = restorePortalScrollOnNextNavigation
        ? (portalScrollPositions.get(pathname) ?? 0)
        : 0;
      restorePortalScrollOnNextNavigation = false;
    });

    return () => {
      window.cancelAnimationFrame(frame);
      portalScrollPositions.set(pathname, pane.scrollTop);
      if (portalScrollPositions.size > 16) {
        const oldestPath = portalScrollPositions.keys().next().value;
        if (oldestPath) portalScrollPositions.delete(oldestPath);
      }
    };
  }, [pathname]);

  useEffect(() => {
    let headerObserver: ResizeObserver | null = null;
    let bodyObserver: MutationObserver | null = null;

    const updateGeometry = () => {
      const main = portalMainRef.current;
      const header = document.querySelector<HTMLElement>(
        ".portal-desktop-header > #prvbuy-global-header",
      );
      if (!main || !header) return false;

      const scale = Number.parseFloat(window.getComputedStyle(main).zoom || "1") || 1;
      const workspaceGutter =
        Number.parseFloat(
          window
            .getComputedStyle(main)
            .getPropertyValue("--portal-workspace-visual-gutter"),
        ) || 0;
      const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
      const headerHeight = header.getBoundingClientRect().height;
      main.style.setProperty(
        "--portal-header-layout-height",
        `${(headerHeight + workspaceGutter) / scale}px`,
      );
      main.style.setProperty(
        "--portal-workspace-layout-height",
        `${Math.max(0, viewportHeight - headerHeight - workspaceGutter) / scale}px`,
      );
      main.style.setProperty("--portal-viewport-layout-height", `${viewportHeight / scale}px`);
      return true;
    };

    const attachGeometry = () => {
      if (!updateGeometry()) return false;
      const header = document.querySelector<HTMLElement>(
        ".portal-desktop-header > #prvbuy-global-header",
      );
      if (header && typeof ResizeObserver !== "undefined") {
        headerObserver?.disconnect();
        headerObserver = new ResizeObserver(updateGeometry);
        headerObserver.observe(header);
      }
      return true;
    };

    document.documentElement.classList.add("portal-desktop-scroll-lock");
    document.body.classList.add("portal-desktop-scroll-lock");
    window.addEventListener("resize", updateGeometry);
    window.visualViewport?.addEventListener("resize", updateGeometry);

    if (!attachGeometry()) {
      bodyObserver = new MutationObserver(() => {
        if (attachGeometry()) {
          bodyObserver?.disconnect();
          bodyObserver = null;
        }
      });
      bodyObserver.observe(document.body, { childList: true, subtree: true });
    }

    return () => {
      headerObserver?.disconnect();
      bodyObserver?.disconnect();
      window.removeEventListener("resize", updateGeometry);
      window.visualViewport?.removeEventListener("resize", updateGeometry);
      document.documentElement.classList.remove("portal-desktop-scroll-lock");
      document.body.classList.remove("portal-desktop-scroll-lock");
    };
  }, []);

  const handlePortalClick = (event: React.MouseEvent<HTMLElement>) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (window.matchMedia("(max-width: 767px)").matches) return;
    const target = event.target instanceof Element ? event.target.closest("a") : null;
    if (!(target instanceof HTMLAnchorElement)) return;

    const href = target.getAttribute("href");
    if (!href) return;

    const nextContext = supportContextFromHref(href);
    if (!nextContext) return;

    event.preventDefault();
    setSupportDrawerPreview(null);
    setSupportDrawerContext(nextContext);
  };

  useEffect(() => {
    const openSupportDrawer = (event: Event) => {
      const detail = (event as CustomEvent<{ href?: string; preview?: SupportDrawerOrderPreview }>).detail;
      const href = detail?.href;
      if (!href) return;
      const nextContext = supportContextFromHref(href);
      if (nextContext) {
        setSupportDrawerPreview(detail.preview ?? null);
        setSupportDrawerContext(nextContext);
      }
    };

    window.addEventListener("prvbuy:open-support-drawer", openSupportDrawer);
    return () => window.removeEventListener("prvbuy:open-support-drawer", openSupportDrawer);
  }, []);

  const shell = (
    <>
      <div className="portal-desktop-header">
        <HomeHeader mobileLayers={false} />
      </div>
      <header
        className="portal-mobile-header"
        aria-label={`${localizedEyebrow}: ${localizedTitle}`}
        style={{
          gridTemplateColumns: showsMobileBack
            ? "88px minmax(0, 1fr) 88px"
            : "44px minmax(0, 1fr) 44px",
        }}
      >
        <div className="portal-mobile-header-actions">
          {showsMobileBack ? (
            <button
              type="button"
              className="portal-mobile-back-trigger"
              aria-label={userCenterCopy.chrome.goBack}
              onClick={() => window.history.back()}
            >
              <ArrowLeft aria-hidden="true" />
            </button>
          ) : null}
          <button
            type="button"
            className="portal-mobile-menu-trigger"
            aria-label={mobileMenuOpen ? userCenterCopy.chrome.closeMenu : userCenterCopy.chrome.openMenu}
            aria-expanded={mobileMenuOpen}
            aria-controls="prv-mobile-account-drawer"
            onClick={() => setMobileMenuOpen((open) => !open)}
          >
            {mobileMenuOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
          </button>
        </div>
        <div className="portal-mobile-title">
          <strong>{localizedEyebrow}</strong>
        </div>
        <span
          className="portal-mobile-header-balance"
          aria-hidden="true"
          style={{ width: showsMobileBack ? 88 : 44 }}
        />
      </header>
      <main
        ref={portalMainRef}
        className={cn(
          "portal-blue-shell min-h-screen bg-[radial-gradient(circle_at_84%_8%,rgba(91,156,255,0.2),transparent_26rem),linear-gradient(180deg,#08111f_0,#14243f_260px,#dce5ee_560px,#fbfaf5_100%)] px-4 pb-16 pt-[148px] text-prv-ink md:px-8 lg:pt-[156px]",
          navStyles.portalShell,
        )}
        onClickCapture={handlePortalClick}
      >
        <div className="portal-shell-layout mx-auto grid w-full max-w-[1474px] gap-6 lg:grid-cols-[290px_minmax(0,1160px)]">
          <aside className={cn("portal-desktop-sidebar hidden lg:block", navStyles.sidebar)}>
            <div className={navStyles.identity}>
              <Link
                href="/user-center/settings"
                className={navStyles.accountEntry}
                aria-label={userCenterCopy.chrome.openSettings}
                aria-current={pathname === "/user-center/settings" || pathname.startsWith("/user-center/settings/") ? "page" : undefined}
                data-active={pathname === "/user-center/settings" || pathname.startsWith("/user-center/settings/") ? "true" : "false"}
              >
                <span className={navStyles.accountAvatar} aria-hidden="true">
                  {session?.name ? accountInitials(session.name) : <UserRound size={23} strokeWidth={1.8} />}
                </span>
                <span className={navStyles.accountCopy}>
                  <span className={navStyles.accountIdentityLine}>
                    <span className={navStyles.accountName}>{session?.name || userCenterCopy.chrome.member}</span>
                    <span className={navStyles.accountBadge}><UserRound aria-hidden="true" /> {userCenterCopy.chrome.account}</span>
                  </span>
                  <span className={navStyles.accountEmail} title={session?.email || "member@example.test"}>
                    {session?.email || "member@example.test"}
                  </span>
                </span>
              </Link>
            </div>

            <nav className={navStyles.navigation} aria-label={userCenterCopy.chrome.userCenterNavigation}>
              {portalNav.map((item) => {
                const Icon = item.icon;
                const isActive = isPortalNavItemActive(pathname, item);
                const count = item.href === "/user-center/after-sales" ? navCounts?.refundsPending : item.href === "/user-center/warehouse" ? navCounts?.warehousePackable : undefined;
                return (
                  <Link
                    href={item.href}
                    key={item.label}
                    aria-current={isActive ? "page" : undefined}
                    className={cn(
                      navStyles.navLink,
                      item.groupStart && navStyles.groupStart,
                      isActive && navStyles.activeLink,
                    )}
                  >
                    <Icon className={navStyles.navIcon} aria-hidden="true" />
                    <span className={navStyles.itemLabel}>
                      <span>{userCenterNavLabel(locale, item.href as keyof typeof userCenterCopy.navigation)}</span>
                      {item.status === "coming-soon" ? (
                        <span className={navStyles.badge}>{userCenterCopy.chrome.comingSoon}</span>
                      ) : count !== undefined || item.href === "/user-center/after-sales" || item.href === "/user-center/warehouse" ? (
                        <span className={navStyles.countBadge} data-loaded={count !== undefined ? "true" : "false"} aria-label={count !== undefined ? (item.href === "/user-center/after-sales" ? userCenterCopy.chrome.pendingAfterSales(count) : userCenterCopy.chrome.packableItems(count)) : undefined}>{count ?? 0}</span>
                      ) : null}
                    </span>
                  </Link>
                );
              })}
              <Link
                href="/user-center/settings"
                aria-current={pathname === "/user-center/settings" || pathname.startsWith("/user-center/settings/") ? "page" : undefined}
                className={cn(
                  navStyles.navLink,
                  (pathname === "/user-center/settings" || pathname.startsWith("/user-center/settings/")) && navStyles.activeLink,
                )}
              >
                <Settings className={navStyles.navIcon} aria-hidden="true" />
                <span className={navStyles.itemLabel}><span>{userCenterCopy.chrome.settings}</span></span>
              </Link>
              <button
                className={navStyles.sidebarLogout}
                type="button"
                disabled={isSigningOut}
                aria-busy={isSigningOut}
                onClick={() => void signOut()}
              >
                <LogOut className="h-5 w-5" aria-hidden="true" />
                <span>{isSigningOut ? userCenterCopy.chrome.loggingOut : userCenterCopy.chrome.logOut}</span>
              </button>
            </nav>
          </aside>
          <div ref={contentPaneRef} className={navStyles.contentPane}>
            <section className="portal-content min-w-0">
              {children}
            </section>
          </div>
        </div>
      </main>
      <MobileAccountDrawer
        open={mobileMenuOpen}
        session={session}
        pathname={pathname}
        onClose={() => setMobileMenuOpen(false)}
        onOpenSearch={(mode) => {
          setMobileMenuOpen(false);
          setMobileSearchMode(mode);
          setMobileSearchOpen(true);
        }}
        onSignOut={() => {
          setMobileMenuOpen(false);
          void signOut();
        }}
        signingOut={isSigningOut}
        navCounts={navCounts}
      />
      <MobileProductSearch
        open={mobileSearchOpen}
        initialMode={mobileSearchMode}
        onClose={() => setMobileSearchOpen(false)}
      />
      <ContextualSupportDrawer
        context={supportDrawerContext}
        initialPreview={supportDrawerPreview}
        onClose={() => {
          setSupportDrawerContext(null);
          setSupportDrawerPreview(null);
        }}
      />
      <div className={navStyles.portalFooter}>
        <SiteFooter />
      </div>
    </>
  );

  return requireAuth && !serverAuthVerified ? <AuthGuard>{shell}</AuthGuard> : shell;
}

export function ShortcutHint() {
  return (
    <span className="hidden items-center gap-1 rounded-[10px] border border-prv-line bg-prv-yellow/70 px-2 py-1 text-xs font-black text-prv-ink sm:flex">
      <Command className="h-3 w-3" /> K
    </span>
  );
}
